import { formatBusinessLocationSummary } from '@/features/quotes/components/quote-steps/business-location';
import {
  useBusinessCoverages,
  useBusinessPlanResourcesByCode,
} from '@/features/quotes/hooks/business';
import { useCatalogLabels } from '@/features/quotes/hooks/common/use-catalog-labels.hook';
import type { Coverage } from '@/services/coverages/types';
import type {
  Proposal,
  ProposalBenefit,
  ProposalCoverage,
  ProposalRisk,
} from '@/services/proposals/types';
import type { CatalogId } from '@/shared/utils/catalog-label.util';
import { getFormattedCurrency } from '@/shared/utils/currencyFormatter';
import { formatZipcode } from '@/shared/utils/format-zipcode.util';
import { Text } from '@noorden-insurtech/alba-design-system';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { ReactNode, useMemo, useState } from 'react';
import { useProductAssistances } from '../../hooks/use-product-assistances.hook';
import {
  useResolvedRiskFields,
  type ResolvedRiskField,
} from '../../hooks/use-resolved-risk-fields.hook';

type Props = {
  proposal: Proposal;
};

const LOCATION_SECTIONS = {
  coverages: 'coverages',
  coveragesBasic: 'coveragesBasic',
  coveragesOptional: 'coveragesOptional',
  assistances: 'assistances',
  questionnaire: 'questionnaire',
} as const;

type LocationSection =
  (typeof LOCATION_SECTIONS)[keyof typeof LOCATION_SECTIONS];

/** Colunas que o campo ocupa no grid de 3 do card. */
const RISK_FIELD_SPAN_CLASS: Record<1 | 2 | 3, string | undefined> = {
  1: undefined,
  2: 'lg:col-span-2',
  3: 'lg:col-span-3',
};

const RISK_FIELD_ROW_COLUMNS = 3;

/**
 * Distribui os campos declarados no registry em linhas de 3 colunas,
 * respeitando o `span` de cada um. A quebra é derivada dos dados — produto
 * novo com outro conjunto de campos não precisa de ajuste de layout.
 */
function packRiskFieldsIntoRows(
  fields: readonly ResolvedRiskField[]
): ResolvedRiskField[][] {
  const rows: ResolvedRiskField[][] = [];
  let currentRow: ResolvedRiskField[] = [];
  let usedColumns = 0;

  for (const field of fields) {
    const span = field.span ?? 1;

    if (currentRow.length > 0 && usedColumns + span > RISK_FIELD_ROW_COLUMNS) {
      rows.push(currentRow);
      currentRow = [];
      usedColumns = 0;
    }

    currentRow.push(field);
    usedColumns += span;
  }

  if (currentRow.length > 0) rows.push(currentRow);

  return rows;
}

/**
 * Questionário de risco empresarial: as duas perguntas de sistemas
 * protecionais vêm de catálogo; as outras duas são Sim/Não do InsureMO.
 * Índices espelham `QUESTIONNAIRE_ITEMS`.
 */
const QUESTION_CATALOGS: ReadonlyArray<CatalogId | 'yes-no'> = [
  'fire-protection-rating-types',
  'yes-no',
  'yes-no',
  'theft-protection-rating-types',
];

function useQuestionnaireAnswerResolver(): ResolveQuestionnaireAnswer {
  const { resolve } = useCatalogLabels([
    'fire-protection-rating-types',
    'theft-protection-rating-types',
  ]);

  return useMemo(() => {
    const resolveCode = (questionIndex: number, code: string): string => {
      const catalog = QUESTION_CATALOGS[questionIndex];

      if (catalog === 'yes-no') {
        if (code === 'Y' || code === 'S') return 'Sim';
        if (code === 'N') return 'Não';
        return code;
      }

      return resolve(catalog, code);
    };

    return (questionIndex, answer) => {
      if (Array.isArray(answer)) {
        return answer.length > 0
          ? answer.map(item => resolveCode(questionIndex, item)).join(', ')
          : '-';
      }
      return answer ? resolveCode(questionIndex, answer) : '-';
    };
  }, [resolve]);
}

export function BusinessLocationsResume({ proposal }: Props) {
  const locations = useMemo(
    () => proposal?.lobs?.flatMap(lob => lob.risks ?? []) ?? [],
    [proposal?.lobs]
  );

  const { data: assistancePlans = [] } = useProductAssistances(
    proposal?.productId
  );

  // Catálogos e tradução de códigos ficam no pai: um card por local
  // instanciaria o mesmo catálogo N vezes.
  const riskFieldsByRiskPk = useResolvedRiskFields(
    proposal?.productCode,
    locations
  );
  const resolveQuestionnaireAnswer = useQuestionnaireAnswerResolver();

  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const totalLocations = locations.length;
  const allExpanded = totalLocations > 0 && expandedIds.size === totalLocations;

  const toggleLocation = (pk: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(pk)) {
        next.delete(pk);
      } else {
        next.add(pk);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (allExpanded) {
      setExpandedIds(new Set());
      return;
    }
    setExpandedIds(new Set(locations.map(location => location.pk)));
  };

  if (totalLocations === 0) {
    return null;
  }

  return (
    <div className='col-span-2 flex flex-col gap-4 self-stretch'>
      <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
        <Text className='text-sm font-bold text-alba-black-500'>
          Lista de Locais
        </Text>

        <button
          type='button'
          onClick={toggleAll}
          aria-label={
            allExpanded
              ? `Fechar Todos ${totalLocations} locais`
              : `Abrir Todos ${totalLocations} locais`
          }
          className='inline-flex items-center gap-2 self-start rounded-lg px-2 py-1 text-xs font-medium text-alba-blue-500'
        >
          {allExpanded
            ? `Fechar Todos (${totalLocations})`
            : `Abrir Todos (${totalLocations})`}
          {allExpanded ? (
            <ChevronUp className='h-4 w-4' strokeWidth={1.6} />
          ) : (
            <ChevronDown className='h-4 w-4' strokeWidth={1.6} />
          )}
        </button>
      </div>

      <div className='space-y-4'>
        {locations.map((location, index) => (
          <BusinessLocationResumeCard
            key={location.pk}
            location={location}
            locationNumber={index + 1}
            isOpen={expandedIds.has(location.pk)}
            onToggle={() => toggleLocation(location.pk)}
            assistancePlans={assistancePlans}
            riskFields={riskFieldsByRiskPk.get(location.pk) ?? []}
            resolveQuestionnaireAnswer={resolveQuestionnaireAnswer}
          />
        ))}
      </div>
    </div>
  );
}

type CardProps = {
  location: ProposalRisk;
  locationNumber: number;
  isOpen: boolean;
  onToggle: () => void;
  assistancePlans: Array<{ code: string; name: string }>;
  /** Campos de "Dados do Risco" já traduzidos — ver `useResolvedRiskFields`. */
  riskFields: ResolvedRiskField[];
  resolveQuestionnaireAnswer: ResolveQuestionnaireAnswer;
};

function BusinessLocationResumeCard({
  location,
  locationNumber,
  isOpen,
  onToggle,
  assistancePlans,
  riskFields,
  resolveQuestionnaireAnswer,
}: CardProps) {
  const locationLabel = `Local ${String(locationNumber).padStart(2, '0')}.`;
  const addressSummary = formatBusinessLocationSummary({
    street: location.address,
    number: location.addressNumber,
    complement: location.addressComplement ?? '',
    neighborhood: location.neighborhood,
    city: location.city,
    state: location.state,
  });

  // Sub-seções abertas dentro do card (coberturas, básicas/opcionais,
  // assistências, questionário). Isolado por card para que cada local
  // mantenha o próprio estado.
  const [openSections, setOpenSections] = useState<Set<LocationSection>>(
    new Set()
  );

  const toggleSection = (section: LocationSection) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const { data: coveragesCatalog = [] } = useBusinessCoverages(
    location.activityTypePerformed
  );

  const { basicCoverages, optionalCoverages, assistanceCoverage } = useMemo(
    () => splitCoveragesByGroup(location.coverages ?? [], coveragesCatalog),
    [location.coverages, coveragesCatalog]
  );

  const resolveCoverageName = useMemo(() => {
    const codeToName = new Map<string, string>();
    for (const item of coveragesCatalog) {
      if (item.code) codeToName.set(item.code.toUpperCase(), item.name);
    }
    return (
      code: string | undefined,
      desc?: string,
      fallback?: string
    ): string => {
      const trimmedDesc = desc?.trim();
      if (trimmedDesc) return trimmedDesc;
      const lookup = code ? codeToName.get(code.toUpperCase()) : undefined;
      return lookup || fallback || code || '-';
    };
  }, [coveragesCatalog]);

  const assistancePackageCode = (
    assistanceCoverage?.planCode ||
    assistanceCoverage?.productElementCode ||
    ''
  ).trim();

  const { data: planResources = [] } = useBusinessPlanResourcesByCode(
    assistancePackageCode || undefined
  );

  const resolvedAssistancePackageName = useMemo(() => {
    if (!assistanceCoverage) return '';
    const desc = assistanceCoverage.productElementDesc?.trim();
    if (desc) return desc;
    const fromPlans = assistancePlans.find(
      plan => plan.code?.trim() === assistancePackageCode
    )?.name;
    return fromPlans || assistancePackageCode || '';
  }, [assistanceCoverage, assistancePlans, assistancePackageCode]);

  const riskFieldRows = useMemo(
    () => packRiskFieldsIntoRows(riskFields),
    [riskFields]
  );

  return (
    <div className='rounded-xl border border-alba-gray-400 p-4'>
      <div className='flex flex-row items-start justify-between gap-2'>
        <div className='min-w-0 space-y-1'>
          <p className='text-xl font-bold text-alba-black-500'>
            {locationLabel}
          </p>
          <p className='text-sm font-bold text-alba-black-500'>
            {addressSummary}
          </p>
        </div>

        <button
          type='button'
          onClick={onToggle}
          aria-label={
            isOpen
              ? `Fechar detalhes do ${locationLabel}`
              : `Abrir detalhes do ${locationLabel}`
          }
          className='inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-alba-black-500 transition-colors hover:bg-alba-gray-100'
        >
          {isOpen ? (
            <ChevronUp className='h-4 w-4' strokeWidth={1.6} />
          ) : (
            <ChevronDown className='h-4 w-4' strokeWidth={1.6} />
          )}
        </button>
      </div>

      {isOpen && (
        <div className='mt-4 space-y-6 border-t border-alba-gray-300 pt-4'>
          <section className='flex flex-col gap-4'>
            <Text className='text-sm font-bold text-alba-black-500'>
              Dados do Risco
            </Text>
            <ResumeCardRow>
              <ResumeCardItem
                label='CEP'
                value={formatZipcode(location.zipCode || '')}
              />
            </ResumeCardRow>
            <ResumeCardRow>
              <ResumeCardItem
                label='Endereço'
                value={location.address || ''}
                className='lg:col-span-3'
              />
            </ResumeCardRow>
            <ResumeCardRow>
              <ResumeCardItem
                label='Número'
                value={location.addressNumber || ''}
              />
              <ResumeCardItem
                label='Complemento'
                value={location.addressComplement || '-'}
                className='lg:col-span-2'
              />
            </ResumeCardRow>
            <ResumeCardRow>
              <ResumeCardItem
                label='Bairro'
                value={location.neighborhood || ''}
              />
              <ResumeCardItem label='Cidade' value={location.city || ''} />
              <ResumeCardItem label='Estado' value={location.state || ''} />
            </ResumeCardRow>
            {riskFieldRows.map(row => (
              <ResumeCardRow key={row.map(field => field.label).join('|')}>
                {row.map(field => (
                  <ResumeCardItem
                    key={field.label}
                    label={field.label}
                    value={field.value}
                    className={RISK_FIELD_SPAN_CLASS[field.span ?? 1]}
                  />
                ))}
              </ResumeCardRow>
            ))}
          </section>

          <section className='flex flex-col gap-3'>
            <Text className='text-sm font-bold text-alba-black-500'>
              Coberturas
            </Text>
            <CoverageGroupCard
              title='Básicas'
              coverages={basicCoverages}
              isOpen={openSections.has(LOCATION_SECTIONS.coveragesBasic)}
              onToggle={() => toggleSection(LOCATION_SECTIONS.coveragesBasic)}
              resolveCoverageName={resolveCoverageName}
            />
            <CoverageGroupCard
              title='Opcionais'
              coverages={optionalCoverages}
              isOpen={openSections.has(LOCATION_SECTIONS.coveragesOptional)}
              onToggle={() =>
                toggleSection(LOCATION_SECTIONS.coveragesOptional)
              }
              resolveCoverageName={resolveCoverageName}
            />
          </section>

          <section className='flex flex-col gap-3'>
            <Text className='text-sm font-bold text-alba-black-500'>
              Assistências/Sorteio
            </Text>
            <AssistancesSummaryCard
              packageName={resolvedAssistancePackageName}
              benefits={assistanceCoverage?.benefits ?? []}
              planResources={planResources}
              isOpen={openSections.has(LOCATION_SECTIONS.assistances)}
              onToggle={() => toggleSection(LOCATION_SECTIONS.assistances)}
            />
          </section>

          <section className='flex flex-col gap-3'>
            <Text className='text-sm font-bold text-alba-black-500'>
              Questionário
            </Text>
            <QuestionnaireSummaryCard
              risk={location}
              isOpen={openSections.has(LOCATION_SECTIONS.questionnaire)}
              onToggle={() => toggleSection(LOCATION_SECTIONS.questionnaire)}
              resolveAnswer={resolveQuestionnaireAnswer}
            />
          </section>
        </div>
      )}
    </div>
  );
}

function ResumeCardRow({ children }: { children: ReactNode }) {
  return (
    <div className='grid grid-cols-1 gap-3 lg:grid-cols-3'>{children}</div>
  );
}

function ResumeCardItem({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className={`flex flex-col gap-1 ${className ?? ''}`}>
      <Text className='text-xs font-bold text-alba-black-500'>{label}</Text>
      <div className='rounded-md border border-alba-gray-300 bg-alba-gray-50 px-3 py-2 text-sm text-alba-gray-600'>
        {value || '-'}
      </div>
    </div>
  );
}

function isAssistanceCoverageCode(code?: string): boolean {
  const upper = (code || '').trim().toUpperCase();
  return upper === 'ASSIST' || upper.endsWith('ASS');
}

function isCapitaCoverageCode(code?: string): boolean {
  const upper = (code || '').trim().toUpperCase();
  return upper === 'CAPITA' || upper.endsWith('CAP');
}

/**
 * Separa as coberturas da proposta nos grupos exibidos no resumo:
 *
 * - `basicCoverages` / `optionalCoverages`: definidas pelo flag `required`
 *   do catálogo de coberturas (`useBusinessCoverages`). Quando o catálogo
 *   ainda não carregou, todas caem como opcionais até a UI rehidratar.
 * - `assistanceCoverage`: a primeira cobertura de assistência/sorteio
 *   (códigos `ASSIST` ou terminados em `ASS`). Capitalização (`CAPITA` /
 *   final `CAP`) é descartada — não faz parte do resumo do segurado.
 */
function splitCoveragesByGroup(
  coverages: ProposalCoverage[],
  catalog: Coverage[]
): {
  basicCoverages: ProposalCoverage[];
  optionalCoverages: ProposalCoverage[];
  assistanceCoverage: ProposalCoverage | undefined;
} {
  const requiredByCode = new Map<string, boolean>();
  for (const item of catalog) {
    if (item.code)
      requiredByCode.set(item.code.toUpperCase(), Boolean(item.required));
  }

  const basic: ProposalCoverage[] = [];
  const optional: ProposalCoverage[] = [];
  let assistance: ProposalCoverage | undefined;

  for (const coverage of coverages) {
    const code = coverage.productElementCode;

    if (isCapitaCoverageCode(code)) continue;
    if (isAssistanceCoverageCode(code)) {
      if (!assistance) assistance = coverage;
      continue;
    }

    const isRequired = requiredByCode.get((code || '').toUpperCase()) ?? false;
    if (isRequired) {
      basic.push(coverage);
    } else {
      optional.push(coverage);
    }
  }

  return {
    basicCoverages: basic,
    optionalCoverages: optional,
    assistanceCoverage: assistance,
  };
}

/* eslint-disable no-unused-vars */
type ResolveCoverageName = (
  _code: string | undefined,
  _desc?: string,
  _fallback?: string
) => string;

type ResolveQuestionnaireAnswer = (
  _questionIndex: number,
  _answer: string | string[] | undefined
) => string;
/* eslint-enable no-unused-vars */

function CoverageGroupCard({
  title,
  coverages,
  isOpen,
  onToggle,
  resolveCoverageName,
}: {
  title: string;
  coverages: ProposalCoverage[];
  isOpen: boolean;
  onToggle: () => void;
  resolveCoverageName: ResolveCoverageName;
}) {
  return (
    <div className='rounded-xl border border-alba-gray-400'>
      <button
        type='button'
        onClick={onToggle}
        aria-label={isOpen ? `Fechar ${title}` : `Abrir ${title}`}
        className='flex w-full items-center justify-between gap-3 border-b border-alba-gray-300 px-4 py-3'
      >
        <Text className='text-sm font-bold text-alba-black-500'>{title}</Text>
        {isOpen ? (
          <ChevronUp className='h-4 w-4' strokeWidth={1.6} />
        ) : (
          <ChevronDown className='h-4 w-4' strokeWidth={1.6} />
        )}
      </button>

      {isOpen && (
        <div className='space-y-2 px-4 py-3'>
          {coverages.length === 0 ? (
            <Text className='text-xs text-alba-gray-600'>
              Nenhuma cobertura {title.toLowerCase()} contratada.
            </Text>
          ) : (
            coverages.map(coverage => (
              <div
                key={coverage.productElementCode || coverage.indemnityPeriod}
                className='flex items-start justify-between gap-4 text-xs text-alba-gray-600'
              >
                <span className='flex-1'>
                  {resolveCoverageName(
                    coverage.productElementCode,
                    coverage.productElementDesc
                  )}
                </span>
                <span className='text-right text-alba-black-500'>
                  {getFormattedCurrency(Number(coverage.sumInsured) || 0)}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function AssistancesSummaryCard({
  packageName,
  benefits,
  planResources,
  isOpen,
  onToggle,
}: {
  packageName: string;
  benefits: ProposalBenefit[];
  planResources: Array<{ code: string; name: string }>;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const title = packageName || 'Sem pacote contratado';

  const resolveBenefitName = (benefit: ProposalBenefit): string => {
    const desc = benefit.productElementDesc?.trim();
    if (desc) return desc;
    const code = String(benefit.productElementCode ?? '').trim();
    if (!code) return '-';
    const fromCatalog = planResources.find(
      resource => String(resource.code || '').trim() === code
    )?.name;
    return fromCatalog?.trim() || code;
  };

  return (
    <div className='rounded-xl border border-alba-gray-400'>
      <div className='flex items-start justify-between gap-3 px-4 py-3'>
        <Text className='text-sm font-bold text-alba-black-500'>{title}</Text>
      </div>

      <button
        type='button'
        onClick={onToggle}
        aria-label={
          isOpen
            ? 'Fechar detalhes das assistências'
            : 'Abrir detalhes das assistências'
        }
        className='flex w-full items-center justify-end gap-2 border-t border-alba-gray-300 px-4 py-2 text-xs text-alba-gray-600'
      >
        <span>Mais Detalhes</span>
        {isOpen ? (
          <ChevronUp className='h-4 w-4' strokeWidth={1.6} />
        ) : (
          <ChevronDown className='h-4 w-4' strokeWidth={1.6} />
        )}
      </button>

      {isOpen && (
        <div className='space-y-2 border-t border-alba-gray-300 px-4 py-3'>
          {benefits.length === 0 ? (
            <Text className='text-xs text-alba-gray-600'>
              Sem benefícios detalhados para este pacote.
            </Text>
          ) : (
            benefits.map(benefit => (
              <div
                key={benefit.productElementCode}
                className='flex items-start justify-between gap-4 text-xs text-alba-gray-600'
              >
                <span className='flex-1'>{resolveBenefitName(benefit)}</span>
                <span className='text-right text-alba-black-500'>
                  {getFormattedCurrency(Number(benefit.sumInsured) || 0)}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Perguntas do questionário de risco empresarial. Espelham os mesmos labels
 * usados no Step 2 da cotação empresarial
 * (`step-risk-business-data.component.tsx`) para manter consistência entre a
 * etapa de preenchimento e o resumo da proposta.
 */
const QUESTIONNAIRE_ITEMS: Array<{
  question: string;
  // eslint-disable-next-line no-unused-vars
  getAnswer: (_risk: ProposalRisk) => string | string[] | undefined;
}> = [
  {
    question: 'Indique os sistemas protecionais contra incêndio.',
    getAnswer: risk => risk.fireProtectionSystems,
  },
  {
    question: 'O local de risco encontra-se em piso superior?',
    getAnswer: risk => risk.isUpperFloor,
  },
  {
    question: 'O imóvel está localizado dentro de shopping?',
    getAnswer: risk => risk.isInsideMall,
  },
  {
    question: 'Indique os sistemas protecionais contra roubo.',
    getAnswer: risk => risk.securityAlarmSystems,
  },
];

function QuestionnaireSummaryCard({
  risk,
  isOpen,
  onToggle,
  resolveAnswer,
}: {
  risk: ProposalRisk;
  isOpen: boolean;
  onToggle: () => void;
  resolveAnswer: ResolveQuestionnaireAnswer;
}) {
  return (
    <div className='rounded-xl border border-alba-gray-400'>
      <button
        type='button'
        onClick={onToggle}
        aria-label={isOpen ? 'Fechar questionário' : 'Abrir questionário'}
        className='flex w-full items-center justify-between gap-3 border-b border-alba-gray-300 px-4 py-3'
      >
        <Text className='text-sm font-bold text-alba-black-500'>
          Respostas do Questionário
        </Text>
        {isOpen ? (
          <ChevronUp className='h-4 w-4' strokeWidth={1.6} />
        ) : (
          <ChevronDown className='h-4 w-4' strokeWidth={1.6} />
        )}
      </button>

      {isOpen && (
        <div className='space-y-3 px-4 py-3'>
          {QUESTIONNAIRE_ITEMS.map(({ question, getAnswer }, index) => (
            <div
              key={question}
              className='grid grid-cols-1 gap-2 lg:grid-cols-[1fr_minmax(260px,380px)] lg:items-center'
            >
              <p className='text-sm text-alba-black-500'>{question}</p>
              <div className='rounded-md border border-alba-gray-300 bg-alba-gray-50 px-3 py-2 text-xs text-alba-gray-600'>
                {resolveAnswer(index, getAnswer(risk))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
